import React, { memo, useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import SEO from "../components/common/SEO";
import { getToken } from "../utils/auth";
import CONFIG from "../config";
import { useTranslation } from "react-i18next";

const About = memo(() => {
  const { t, i18n } = useTranslation();
  const [aboutUs, setAboutUs] = useState([]);
  const [loading, setLoading] = useState(true);
  const isArabic = i18n.language?.startsWith('ar');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${CONFIG.API_URL}/api/about-us`, {
          headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {}
        });
        setAboutUs(res.data || []);
      } catch (err) {
        console.error("Failed to load homepage content.", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="text-center py-40"><i className="fas fa-spinner fa-spin text-4xl text-blue-600"></i></div>;
  }

  return (
    <>
      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
      </Helmet>
      <SEO title={isArabic ? "من نحن" : "About Us"} />

      <div className="relative overflow-hidden pt-24">
        {/* Decorative Background */}
        <div className={`absolute top-0 ${isArabic ? '-left-20' : '-right-20'} w-96 h-96 bg-brand-primary/5 rounded-full blur-3xl -z-10`} />

        <section className="max-w-5xl mx-auto py-24 px-6 text-gray-800">
          {(() => {
            const activeAbout = aboutUs.find(a => a?.is_active);
            if (activeAbout) {
              return (
                <div key={activeAbout.id} className="animate-in fade-in slide-in-from-bottom-8 duration-1000">
                  <div className="text-center mb-20">
                    <span className="text-xs font-black uppercase tracking-[0.3em] text-brand-primary mb-4 block animate-fade-in">
                      {isArabic ? activeAbout.suptitle_ar : activeAbout.suptitle}
                    </span>
                    <h1 className="text-5xl lg:text-7xl font-black text-gray-900 mb-8 drop-shadow-sm tracking-tight">
                      {isArabic ? activeAbout.title_ar : activeAbout.title}
                    </h1>
                    <div className="w-24 h-2 bg-brand-primary mx-auto rounded-full" />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div className="space-y-8 text-lg leading-relaxed text-gray-600">
                      <p className="font-bold text-gray-900 text-2xl leading-snug">
                        {isArabic ? activeAbout.description_ar : activeAbout.description}
                      </p>
                      <p className="text-xl">
                        {isArabic ? activeAbout.value_ar : activeAbout.value}
                      </p>
                      <div
                        className="p-10 bg-brand-primary text-white relative overflow-hidden group hover:-translate-y-2 transition-transform duration-500 shadow-2xl shadow-brand-primary/20"
                        style={{ borderRadius: 'var(--card-radius)' }}
                      >
                        <div className={`${isArabic
                          ? "absolute top-0 left-0 p-8 opacity-10 text-6xl group-hover:scale-110 transition-transform"
                          : "absolute top-0 right-0 p-8 opacity-10 text-6xl group-hover:scale-110 transition-transform"
                          }`}>
                          <i className={`fas ${isArabic ? 'fa-quote-left' : 'fa-quote-right'}`}></i>
                        </div>
                        <h4 className="text-xs font-black uppercase tracking-widest mb-4 opacity-70">{t('about.purpose', 'Our Purpose')}</h4>
                        <p className="relative z-10 text-xl font-medium italic">
                          {isArabic ? activeAbout.mission_ar : activeAbout.mission}
                        </p>
                      </div>
                    </div>

                    <div className="relative group perspective-1000">
                      <div className="absolute -inset-6 bg-brand-primary/10 rounded-[60px] blur-3xl group-hover:bg-brand-primary/20 transition-all duration-700" />
                      <div className="relative rounded-[50px] overflow-hidden shadow-2xl transform transition-all duration-700 group-hover:scale-[1.03] active:scale-95">
                        <img
                          src={activeAbout.image_url}
                          alt={isArabic ? activeAbout.title_ar : activeAbout.title}
                          className="w-full h-full object-cover aspect-square"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      </div>

                      {/* Floating Stat Card */}
                      <div
                        className={`absolute -bottom-10 ${isArabic ? '-left-10' : '-right-10'} p-8 hidden sm:block animate-bounce-slow`}
                        style={{ backgroundColor: 'var(--bg-header)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', border: '1px solid var(--border)' }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-brand-primary rounded-2xl flex items-center justify-center text-white text-xl shadow-lg shadow-brand-primary/30">
                            {/* Render icon class from DB */}
                            <i className={activeAbout.icon || "fas fa-history"}></i>
                          </div>
                          <div>
                            <div className="text-2xl font-black text-gray-900">
                              {activeAbout.experience_years}+
                            </div>
                            <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest">
                              {isArabic ? activeAbout.experience_text_ar : activeAbout.experience_text}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            // return (
            //   // <>
            //   //   <div className="text-center mb-20">
            //   //     <span className="text-xs font-black uppercase tracking-[0.3em] text-brand-primary mb-4 block animate-fade-in">{t('about.heritage', 'Our Heritage')}</span>
            //   //     <h1 className="text-5xl lg:text-7xl font-black text-gray-900 mb-8 drop-shadow-sm tracking-tight">
            //   //       {t('about.title')}
            //   //     </h1>
            //   //     <div className="w-24 h-2 bg-brand-primary mx-auto rounded-full" />
            //   //   </div>

            //   //   <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            //   //     <div className="space-y-8 text-lg leading-relaxed text-gray-600">
            //   //       <p className="font-bold text-gray-900 text-2xl leading-snug">
            //   //         {t('about.description')}
            //   //       </p>
            //   //       <p className="text-xl">
            //   //         {t('about.value')}
            //   //       </p>
            //   //       <div
            //   //         className="p-10 bg-brand-primary text-white relative overflow-hidden group shadow-2xl shadow-brand-primary/20"
            //   //         style={{ borderRadius: 'var(--card-radius)' }}
            //   //       >
            //   //         <div className={isArabic
            //   //           ? "absolute top-0 left-0 p-8 opacity-10 text-6xl group-hover:scale-110 transition-transform"
            //   //           : "absolute top-0 right-0 p-8 opacity-10 text-6xl group-hover:scale-110 transition-transform"
            //   //         }>
            //   //           {isArabic
            //   //             ? (<i className="fas fa-quote-left"></i>)
            //   //             : (<i className="fas fa-quote-right"></i>)
            //   //           }                      </div>
            //   //         <h4 className="text-xs font-black uppercase tracking-widest mb-4 opacity-70">{t('about.purpose', 'Our Purpose')}</h4>
            //   //         <p className="relative z-10 text-xl font-medium italic">
            //   //           {t('about.mission')}
            //   //         </p>
            //   //       </div>
            //   //     </div>

            //   //     <div className="relative group perspective-1000">
            //   //       <div className="absolute -inset-6 bg-brand-primary/10 rounded-[60px] blur-3xl group-hover:bg-brand-primary/20 transition-all duration-700" />
            //   //       <div className="relative rounded-[50px] overflow-hidden shadow-2xl transform transition-all duration-700 group-hover:scale-[1.03] active:scale-95">
            //   //         <img
            //   //           src="https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?auto=format&fit=crop&q=80&w=1200"
            //   //           alt="Luxury Furniture Craftsmanship"
            //   //           className="w-full h-full object-cover aspect-square"
            //   //         />
            //   //         <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            //   //       </div>

            //   //       {/* Floating Stat Card */}
            //   //       <div
            //   //         className={`absolute -bottom-10 ${isArabic ? '-left-10' : '-right-10'} p-8 hidden sm:block animate-bounce-slow`}
            //   //         style={{ backgroundColor: 'var(--bg-header)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', border: '1px solid var(--border)' }}
            //   //       >
            //   //         <div className={isArabic
            //   //           ? "flex flex-row-reverse items-center gap-4"
            //   //           : "flex items-center gap-4"
            //   //         }>
            //   //           <div className="w-12 h-12 bg-brand-primary rounded-2xl flex items-center justify-center text-white text-xl shadow-lg shadow-brand-primary/30">
            //   //             <i className="fas fa-history"></i>
            //   //           </div>
            //   //           <div>
            //   //             <div className="text-2xl font-black text-gray-900">{isArabic ? t('about.experience_years_ar') : t('about.experience_years_en')}</div>
            //   //             <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest">{isArabic ? t('about.experience_text_ar') : t('about.experience_text_en')}</div>
            //   //           </div>
            //   //         </div>
            //   //       </div>
            //   //     </div>
            //   //   </div >
            //   // </>

            // );
          })()}
        </section>
      </div >
    </>
  );
});

export default About;
